import { jsx as _jsx } from "react/jsx-runtime";
import { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { Spinner } from '@epam/loveship';
import { useUuiContext, UuiError } from '@epam/uui-core';
import { useNavigator } from '../hooks/use-navigator';
import { CountryCode, EventKind, RequestKind, } from '../types';
import { ALLOWED_PORTAL_URLS, COUNTRY_MAP, IS_ON_IFRAME, VERSION } from '../constants';
import { parentPostMessage } from '../utils';
import { useAnalytics } from '../hooks';
import { NoPlatformErrorPage } from '../components/no-platform-error-page';
const PortalContext = createContext({
    country: null,
    baseUrl: '',
    locale: 'en',
    debug: false,
    postMessage: () => null,
});
export const PortalProvider = ({ children, scope, moduleInfo = {}, supportedLocales, ctaButtons = [], customActions = {}, defaultCountryCode = CountryCode.MONTENEGRO, }) => {
    const postMessage = parentPostMessage(scope);
    const { uuiErrors } = useUuiContext();
    const { navigate, isNavigationInitialized } = useNavigator(postMessage);
    const [country, setCountry] = useState(null);
    const [baseUrl, setBaseUrl] = useState('');
    const [locale, setLocale] = useState('en');
    const [user, setUser] = useState(null);
    const [debug, setDebug] = useState(false);
    if (!IS_ON_IFRAME && process.env.REACT_APP_DISABLE_LOCAL_MODULE !== 'true') {
        uuiErrors.reportError(new UuiError({
            title: `Oops! It seems you've landed here via an outdated link.`,
            subtitle: _jsx(NoPlatformErrorPage, { isLocal: location.hostname === 'localhost' }),
        }));
    }
    useAnalytics(user, debug);
    const changeCountry = ({ countryCode, platformBaseUrl }) => {
        if (countryCode) {
            const upperCountryCode = countryCode.toUpperCase();
            const countryName = COUNTRY_MAP[upperCountryCode];
            setCountry({ code: upperCountryCode, name: countryName || countryCode });
            if (!countryName) {
                console.error(`Unknown countryCode: ${countryCode}`);
            }
        }
        if (platformBaseUrl) {
            setBaseUrl(platformBaseUrl?.replace(/\/*$/, ''));
        }
    };
    const init = (message) => {
        const { location: newLocation, locale: newLocale = 'en', userIdHash, userCountryCode, isPlatformDeveloper, debug, } = message;
        changeCountry(message);
        setLocale(newLocale);
        setUser({
            userIdHash,
            userCountryCode,
            isPlatformDeveloper,
        });
        navigate(newLocation);
        setDebug(debug);
    };
    const handleEventKind = useCallback((message) => {
        switch (message.eventKind) {
            case EventKind.INIT:
                init(message);
                break;
            case EventKind.COUNTRY_CHANGE:
                changeCountry(message);
                break;
            case EventKind.LOCATION_CHANGE:
                if (message.location) {
                    navigate(message.location);
                }
                break;
            case EventKind.LOCALE_CHANGE:
                if (message.locale) {
                    setLocale(message.locale);
                }
                break;
            case EventKind.CTA_BUTTON_CLICKED:
                customActions?.[message.action]?.();
        }
    }, []);
    const handler = useCallback((event) => {
        // todo: remove wildcard when all envs are setup
        if (ALLOWED_PORTAL_URLS.some(url => url === event.origin || url === '*')) {
            const message = event.data;
            console.log(scope, 'GET MESSAGE', message, ALLOWED_PORTAL_URLS.join(','));
            handleEventKind(message);
        }
    }, [handleEventKind]);
    useEffect(() => {
        window.addEventListener('message', handler);
        return () => {
            window.removeEventListener('message', handler);
        };
    }, [handler]);
    useEffect(() => {
        if (!supportedLocales || Object.keys(supportedLocales).length) {
            postMessage({
                requestKind: RequestKind.INIT,
                moduleInfo: { version: VERSION, ...moduleInfo },
                supportedLocales,
                ctaButtons,
            });
            if (!IS_ON_IFRAME) {
                setCountry({ code: defaultCountryCode, name: COUNTRY_MAP[defaultCountryCode] });
            }
        }
    }, [supportedLocales]);
    if (IS_ON_IFRAME && (!country || !isNavigationInitialized)) {
        return _jsx(Spinner, {});
    }
    return (_jsx(PortalContext.Provider, { value: { country, baseUrl, locale, debug, postMessage }, children: children }));
};
export const usePortalContext = () => {
    const portalContext = useContext(PortalContext);
    if (!portalContext && IS_ON_IFRAME) {
        throw new Error('usePortalContext must be within PortalProvider');
    }
    return portalContext;
};
